import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  WorkspaceContext,
  WorkspaceDhisInstance,
  saveSelectedInstanceEnvironment,
  loadSelectedInstanceEnvironment
} from "../lib/api.js";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; context: WorkspaceContext; instances: WorkspaceDhisInstance[] };

export default function SelectInstance() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [selected, setSelected] = useState<string | null>(loadSelectedInstanceEnvironment());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [context, list] = await Promise.all([
          api.currentWorkspace(),
          api.workspaceDhisInstances()
        ]);
        if (cancelled) return;
        setState({ status: "ready", context, instances: list.instances });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to load workspace";
        setState({ status: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSelect = (environment: string) => {
    setSelected(environment);
    saveSelectedInstanceEnvironment(environment);
  };

  const onContinue = () => {
    navigate("/app", { replace: true });
  };

  if (state.status === "loading") {
    return <div className="content"><p className="muted">Loading workspace…</p></div>;
  }
  if (state.status === "error") {
    return (
      <div className="content">
        <div className="alert alert-error">{state.message}</div>
      </div>
    );
  }

  const { context, instances } = state;
  const subscriptionBlocked = !context.subscriptionActive;
  const selectedInstance = instances.find((i) => i.environment === selected) ?? null;
  const canContinue = Boolean(selectedInstance) && (selectedInstance?.subscriptionAvailable ?? false);

  return (
    <div className="content">
      <div className="page-head">
        <h1>Select a DHIS2 instance</h1>
        <p className="muted">
          Choose a configured DHIS2 instance to monitor tally streams for this workspace.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head">
          <strong>{context.workspace.organizationName}</strong>
          <span className="pill pill-info" style={{ marginLeft: 8 }}>
            {context.workspace.workspaceName}
          </span>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span className="pill">Status: {context.workspace.status}</span>
            <span className={`pill ${context.subscriptionActive ? "pill-success" : "pill-warning"}`}>
              Subscription: {context.subscription?.status ?? "none"}
            </span>
            {context.plan && <span className="pill">Plan: {context.plan.name}</span>}
          </div>
        </div>
      </div>

      {subscriptionBlocked && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          Your current subscription does not permit tally stream monitoring. Please upgrade or contact your
          workspace administrator.
        </div>
      )}

      {instances.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p><strong>No DHIS2 instances connected yet.</strong></p>
            <p className="muted">
              An administrator can add one on the <a href="/app/instances">DHIS2 instances</a> page.
              Once added, use <em>Detect API</em> to verify the URL and <em>Discover app</em> to
              auto-provision the TallyStream app client.
            </p>
            <div style={{ marginTop: 12 }}>
              <a href="/app/instances" className="btn btn-primary">Go to DHIS2 instances</a>
            </div>
          </div>
        </div>
      ) : (
        <div className="instance-grid" style={{ display: "grid", gap: 12 }}>
          {instances.map((instance) => {
            const blocked = !instance.subscriptionAvailable;
            const isSelected = instance.environment === selected;
            return (
              <button
                key={instance.environment}
                type="button"
                onClick={() => !blocked && onSelect(instance.environment)}
                disabled={blocked}
                className={`card ${isSelected ? "card-selected" : ""}`}
                style={{
                  textAlign: "left",
                  cursor: blocked ? "not-allowed" : "pointer",
                  opacity: blocked ? 0.6 : 1,
                  borderColor: isSelected ? "var(--accent, #2563eb)" : undefined
                }}
              >
                <div className="card-head">
                  <strong>{instance.label}</strong>
                  <span className="pill" style={{ marginLeft: 8 }}>
                    {instance.environmentType}
                  </span>
                  {!instance.enabled && (
                    <span className="pill pill-warning" style={{ marginLeft: 8 }}>
                      disabled
                    </span>
                  )}
                  {blocked && instance.enabled && (
                    <span className="pill pill-warning" style={{ marginLeft: 8 }}>
                      subscription blocked
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <div className="muted" style={{ fontSize: 12 }}>{instance.baseUrl}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    Auth mode: {instance.authMode}
                    {" · "}
                    Secret: {instance.secretConfigured ? "configured" : "missing"}
                    {instance.lastSuccessfulConnection && (
                      <>{" · "}Last ok: {instance.lastSuccessfulConnection}</>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canContinue}
          onClick={onContinue}
        >
          Continue to workspace
        </button>
        <button type="button" className="btn" onClick={() => navigate("/app")}>
          Skip for now
        </button>
      </div>
    </div>
  );
}
